import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, Lock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const REASON_COPY = {
  not_found:    "This reset link isn't valid. It may have been superseded by a newer one.",
  already_used: "This reset link has already been used. If you still need to reset your password, request a new link.",
  expired:      "This reset link has expired. Request a new one to try again.",
  malformed:    "This reset link is malformed. Request a new one to try again.",
};

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [inspect, setInspect] = useState(null);   // { valid, email_hint? } | { valid: false, reason }
  const [inspectErr, setInspectErr] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/inspect/${token}`)
      .then((r) => setInspect(r.data))
      .catch((e) => setInspectErr(e?.response?.data?.detail || "Could not verify link"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2)     { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: pw });
      // Backend signs the user in on success — save the returned token
      // so they land on /app already authenticated.
      localStorage.setItem("sw_token", r.data.token);
      localStorage.setItem("sw_user", JSON.stringify(r.data.user));
      toast.success("Password updated");
      window.location.href = "/app/dashboard";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset password");
      setBusy(false);
    }
  };

  // Loading / error / invalid states — render before the form to keep
  // main render path simple.
  if (inspectErr) return <Shell>{ErrorCard(inspectErr)}</Shell>;
  if (!inspect) return <Shell>
    <div className="w-full max-w-sm text-center text-slate-400">
      <Loader2 size={18} className="animate-spin mx-auto mb-2" />
      Verifying link…
    </div>
  </Shell>;
  if (!inspect.valid) return <Shell>{ErrorCard(REASON_COPY[inspect.reason] || "This reset link is not valid.")}</Shell>;

  return (
    <Shell>
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="reset-form">
        <div className="w-12 h-12 rounded-full bg-blue-50 grid place-items-center mb-4">
          <Lock size={20} className="text-blue-600" />
        </div>
        <div className="label-eyebrow mb-2">Set a new password</div>
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Choose a new password</h2>
        {inspect.email_hint && (
          <p className="text-slate-600 mt-2 text-[14px]">for <strong>{inspect.email_hint}</strong></p>
        )}

        <div className="mt-6 space-y-4">
          <div>
            <label className="label-eyebrow">New password</label>
            <input
              type="password" required autoFocus minLength={6}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 6 characters"
              className="mt-1.5 w-full h-10 px-3 border border-slate-200 rounded-sm bg-white outline-none focus:border-slate-900 transition text-[14px]"
              data-testid="reset-pw"
            />
          </div>
          <div>
            <label className="label-eyebrow">Confirm password</label>
            <input
              type="password" required minLength={6}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className={"mt-1.5 w-full h-10 px-3 border rounded-sm bg-white outline-none focus:border-slate-900 transition text-[14px] " +
                (pw2 && pw !== pw2 ? "border-red-400" : "border-slate-200")}
              data-testid="reset-pw2"
            />
            {pw2 && pw !== pw2 && (
              <p className="text-[11.5px] text-red-600 mt-1">Passwords don't match.</p>
            )}
          </div>
          <button
            type="submit" disabled={busy || pw.length < 6 || pw !== pw2}
            className="w-full h-10 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-medium text-[14px] rounded-sm transition disabled:opacity-60"
            data-testid="reset-submit"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {busy ? "Setting new password…" : "Set new password & sign in"}
          </button>
          <p className="text-[12px] text-slate-500">
            After you set a new password, any other outstanding reset links for this account become invalid.
          </p>
        </div>
      </form>
    </Shell>
  );

  function ErrorCard(msg) {
    return (
      <div className="w-full max-w-sm">
        <div className="w-12 h-12 rounded-full bg-red-50 grid place-items-center mb-4">
          <AlertTriangle size={22} className="text-red-600" />
        </div>
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Reset link unavailable</h2>
        <p className="text-slate-600 mt-2 text-[14px] leading-relaxed">{msg}</p>
        <div className="mt-6 flex gap-2">
          <Link to="/forgot-password" className="h-10 px-4 inline-flex items-center bg-slate-900 hover:bg-slate-800 text-white text-[13.5px] font-medium rounded-sm transition">
            Request a new link
          </Link>
          <Link to="/login" className="h-10 px-4 inline-flex items-center border border-slate-200 hover:border-slate-400 text-[13.5px] rounded-sm transition">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }
}

function Shell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--sw-bg)]">
      <div className="hidden lg:block relative overflow-hidden border-r border-slate-200">
        <img
          src="https://images.unsplash.com/photo-1770009971150-f50bc7d373a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGdlb21ldHJpYyUyMGxpZ2h0JTIwYXJjaGl0ZWN0dXJlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc2ODE4NTJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-white/30 to-transparent" />
        <div className="relative z-10 p-10 flex flex-col h-full">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-slate-900 grid place-items-center">
              <Sparkles size={18} className="text-white" />
            </div>
            <div className="font-heading text-lg font-semibold">Brainydo</div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">{children}</div>
    </div>
  );
}
